import React from 'react';
import { Loader2, AlertCircle, Maximize2, X, ExternalLink } from 'lucide-react';

/**
 * Cadre d'aperçu commun aux documents du dossier de Suivi de prestation (devis envoyé, devis signé,
 * contrat d'abonnement). 2026-09-06, Emeni : « l'aperçu du contrat est tout petit — on devrait avoir
 * l'aperçu avec un bouton qui ouvre une modale d'aperçu, et aussi un bouton d'aperçu du devis qu'il avait
 * reçu ». L'aperçu reste donc affiché en place (jamais un simple lien qui renvoie ailleurs), mais avec une
 * barre de titre qui permet de l'ouvrir en grand sans quitter la page.
 */
export function CadreApercu({
    titre, url, estImage = false, chargement = false, erreur = '', hauteur = 'h-[46vh]',
}: {
    titre: string;
    url: string;
    estImage?: boolean;
    chargement?: boolean;
    erreur?: string;
    hauteur?: string;
}) {
    const [plein, setPlein] = React.useState(false);
    const pret = !chargement && !erreur && !!url;

    const contenu = (classes: string) => (estImage
        ? <img src={url} alt={titre} className={`${classes} object-contain mx-auto`} />
        : <iframe src={url} title={titre} className={`${classes} w-full rounded-lg`} />);

    return (
        <>
            <div className="rounded-xl border border-border bg-surface-soft overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
                    <p className="text-[11px] font-bold text-content-muted truncate" title={titre}>{titre}</p>
                    {pret && (
                        <div className="flex items-center gap-1 shrink-0">
                            <button type="button" onClick={() => setPlein(true)}
                                className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                <Maximize2 size={12} /> Agrandir
                            </button>
                            <a href={url} target="_blank" rel="noreferrer" title="Ouvrir dans un nouvel onglet"
                                className="text-content-muted hover:text-content p-1"><ExternalLink size={12} /></a>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-center min-h-[220px] overflow-auto p-2">
                    {chargement ? (
                        <Loader2 className="animate-spin text-content-muted" size={22} />
                    ) : erreur ? (
                        <p className="text-xs text-red-500 font-semibold flex items-center gap-1.5 p-4"><AlertCircle size={13} /> {erreur}</p>
                    ) : contenu(hauteur)}
                </div>
            </div>

            {plein && (
                <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setPlein(false)}>
                    <div className="w-[95vw] h-[92vh] bg-surface border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border shrink-0">
                            <p className="text-sm font-bold text-content truncate">{titre}</p>
                            <div className="flex items-center gap-3 shrink-0">
                                <a href={url} target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                    <ExternalLink size={13} /> Nouvel onglet
                                </a>
                                <button onClick={() => setPlein(false)} className="text-content-muted hover:text-content"><X size={20} /></button>
                            </div>
                        </div>
                        <div className="flex-1 min-h-0 p-3 overflow-auto flex items-center justify-center">
                            {contenu('h-full max-h-full')}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/**
 * Aperçu du devis tel que le client l'a reçu — servi par le lien public du devis, donc directement
 * affichable sans authentification.
 */
export function DevisEnvoyeApercu({ token, nomFichier }: { token: string; nomFichier?: string }) {
    return (
        <CadreApercu
            titre={nomFichier || 'Devis envoyé au client'}
            url={`/api/prestation-devis/${token}/document`}
        />
    );
}

/**
 * Tous les documents du dossier, consultables À CHAQUE ÉTAPE (2026-09-06, Emeni : « même à l'étape d'après
 * on doit toujours avoir le devis consultable dans le suivi, à chaque étape suivante on garde une trace de
 * l'étape d'avant »). Les panneaux d'étape ne portent donc plus que les ACTIONS ; les aperçus vivent ici,
 * en onglets, et ne restent jamais derrière soi quand le dossier avance.
 *
 * Un seul onglet est monté à la fois : l'aperçu du contrat régénère le PDF à la volée, inutile de le
 * fabriquer tant qu'on ne le regarde pas.
 */
export function DocumentsDuDossier({
    dossierId, devisToken, devisDocument, devisSigneDocument, subscriptionId, subscriptionSignedDocument,
    ApercuDevisSigne, ApercuContrat, ApercuContratSigne,
}: {
    dossierId: string;
    devisToken?: string | null;
    devisDocument?: { file_name: string } | null;
    devisSigneDocument?: { id: string; file_name: string; mime_type?: string } | null;
    subscriptionId?: string | null;
    // Contrat SIGNÉ (2026-09-26, demande d'Emeni) : distinct du contrat régénéré ci-dessous, même principe
    // que "Devis envoyé" / "Devis signé" — l'onglet "Contrat d'abonnement" ne montrait jusqu'ici jamais
    // l'exemplaire réellement signé par le client, seulement l'original reconstitué à la volée.
    subscriptionSignedDocument?: { file_name?: string } | null;
    ApercuDevisSigne: React.ComponentType<{ dossierId: string; document: any }>;
    ApercuContrat: React.ComponentType<{ subscriptionId: string }>;
    ApercuContratSigne?: React.ComponentType<{ subscriptionId: string; fileName?: string }>;
}) {
    const onglets = [
        ...(devisDocument && devisToken ? [{ cle: 'devis', libelle: 'Devis envoyé' }] : []),
        ...(devisSigneDocument ? [{ cle: 'devis_signe', libelle: 'Devis signé' }] : []),
        ...(subscriptionId ? [{ cle: 'contrat', libelle: "Contrat d'abonnement" }] : []),
        ...(subscriptionId && subscriptionSignedDocument && ApercuContratSigne ? [{ cle: 'contrat_signe', libelle: 'Contrat signé' }] : []),
    ];
    // Par défaut, le document le plus avancé du dossier — c'est celui qu'on vient de produire.
    const [actif, setActif] = React.useState('');
    const cleParDefaut = onglets.length > 0 ? onglets[onglets.length - 1].cle : '';
    React.useEffect(() => { setActif((a) => (a && onglets.some((o) => o.cle === a) ? a : cleParDefaut)); }, [cleParDefaut]); // eslint-disable-line react-hooks/exhaustive-deps

    if (onglets.length === 0) return null;

    return (
        <div className="bg-surface border border-border rounded-2xl p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-bold text-content mr-1">Documents du dossier</p>
                {onglets.map((o) => (
                    <button key={o.cle} type="button" onClick={() => setActif(o.cle)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                            actif === o.cle ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                        {o.libelle}
                    </button>
                ))}
            </div>
            {actif === 'devis' && devisToken && (
                <DevisEnvoyeApercu token={devisToken} nomFichier={devisDocument?.file_name} />
            )}
            {actif === 'devis_signe' && devisSigneDocument && (
                <ApercuDevisSigne dossierId={dossierId} document={devisSigneDocument} />
            )}
            {actif === 'contrat' && subscriptionId && (
                <ApercuContrat subscriptionId={subscriptionId} />
            )}
            {actif === 'contrat_signe' && subscriptionId && subscriptionSignedDocument && ApercuContratSigne && (
                <ApercuContratSigne subscriptionId={subscriptionId} fileName={subscriptionSignedDocument.file_name} />
            )}
        </div>
    );
}
